import type { ProfileCardRarity } from '../types/profileCard';
import { PROFILE_CARD_RARITIES, parseProfileCardRarity } from '../types/profileCard';

/** 등급별 고정 총 확률(%) — 합 100 */
export const PROFILE_CARD_TIER_PROBABILITY_PERCENT: Record<ProfileCardRarity, number> = {
  normal: 65,
  rare: 20,
  epic: 10,
  legendary: 5,
};

/** 등급별 중복 환급률(세트 구입가 대비) */
export const PROFILE_CARD_TIER_EXCHANGE_RATE: Record<ProfileCardRarity, number> = {
  normal: 0.1,
  rare: 0.3,
  epic: 0.5,
  legendary: 1.2,
};

export const PROFILE_CARD_RARITY_LABEL: Record<ProfileCardRarity, string> = {
  normal: '보통',
  rare: '레어',
  epic: '희귀',
  legendary: '초희귀',
};

/** 등급 UI 색상 — 과제 상태 4색과 동일(원형 도형·텍스트만 사용) */
export const PROFILE_CARD_RARITY_COLORS: Record<ProfileCardRarity, string> = {
  /** 보통 ← 지연(#65A994) */
  normal: '#65A994',
  /** 레어 ← 확인(#227971) */
  rare: '#227971',
  /** 희귀 ← 예외(#FFCC6E) */
  epic: '#FFCC6E',
  /** 초희귀 ← 미확인(#FF316B) */
  legendary: '#FF316B',
};

/** 높은 등급 → 낮은 등급 순(빈 등급 확률 이관용) */
const RARITY_HIGH_TO_LOW: ProfileCardRarity[] = ['legendary', 'epic', 'rare', 'normal'];

export type ProfileCardRaritySlotLike = {
  rarity: ProfileCardRarity | null;
  /** 이미지 등록 여부 */
  hasImage: boolean;
};

export function profileCardExchangeCoinsForRarity(
  setPriceCoins: number,
  rarity: ProfileCardRarity | null | undefined
): number {
  if (!rarity) return 0;
  const rate = PROFILE_CARD_TIER_EXCHANGE_RATE[rarity] ?? 0;
  return Math.max(0, Math.round(Math.max(0, setPriceCoins) * rate));
}

/** 세트에 해당 등급 카드가 없으면 그 확률을 바로 아래(존재하는) 등급으로 이관 */
export function computeEffectiveTierProbabilities(
  slots: ProfileCardRaritySlotLike[]
): Record<ProfileCardRarity, number> {
  const counts: Record<ProfileCardRarity, number> = {
    normal: 0,
    rare: 0,
    epic: 0,
    legendary: 0,
  };
  for (const s of slots) {
    if (!s.hasImage || !s.rarity) continue;
    counts[s.rarity] += 1;
  }

  const probs: Record<ProfileCardRarity, number> = { ...PROFILE_CARD_TIER_PROBABILITY_PERCENT };

  for (const tier of RARITY_HIGH_TO_LOW) {
    if (tier === 'normal') continue;
    if (counts[tier] > 0) continue;
    const orphan = probs[tier];
    probs[tier] = 0;
    if (orphan <= 0) continue;
    const idx = RARITY_HIGH_TO_LOW.indexOf(tier);
    let moved = false;
    for (let i = idx + 1; i < RARITY_HIGH_TO_LOW.length; i++) {
      const lower = RARITY_HIGH_TO_LOW[i];
      if (counts[lower] > 0 || lower === 'normal') {
        probs[lower] += orphan;
        moved = true;
        break;
      }
    }
    if (!moved) probs.normal += orphan;
  }

  // 보통도 비어 있으면(유효성 실패 상태) 확률은 남겨 두되 뽑기는 실패 처리
  return probs;
}

/** 각 슬롯의 최종 뽑힘 확률(%). 이미지·등급 없으면 0 */
export function computeSlotDropPercents(slots: ProfileCardRaritySlotLike[]): number[] {
  const counts: Record<ProfileCardRarity, number> = {
    normal: 0,
    rare: 0,
    epic: 0,
    legendary: 0,
  };
  for (const s of slots) {
    if (!s.hasImage || !s.rarity) continue;
    counts[s.rarity] += 1;
  }
  const tierProb = computeEffectiveTierProbabilities(slots);
  return slots.map((s) => {
    if (!s.hasImage || !s.rarity) return 0;
    const n = counts[s.rarity];
    if (n <= 0) return 0;
    return tierProb[s.rarity] / n;
  });
}

/** 저장 유효성: 이미지가 있는 보통 등급 카드 ≥ 1 */
export function validateProfileCardSetRarities(slots: ProfileCardRaritySlotLike[]): string | null {
  const hasNormal = slots.some((s) => s.hasImage && s.rarity === 'normal');
  if (!hasNormal) {
    return '이미지가 등록된 「보통」 등급 카드를 최소 1장 이상 넣어 주세요.';
  }
  const incomplete = slots.some((s) => s.hasImage && !s.rarity);
  if (incomplete) {
    return '이미지가 있는 카드는 등급을 모두 선택해 주세요.';
  }
  return null;
}

export function formatExchangeRatePercent(rarity: ProfileCardRarity): string {
  return `${Math.round(PROFILE_CARD_TIER_EXCHANGE_RATE[rarity] * 100)}%`;
}

export function raritySelectOptions(): Array<{ value: ProfileCardRarity; label: string }> {
  return PROFILE_CARD_RARITIES.map((r) => ({
    value: r,
    label: PROFILE_CARD_RARITY_LABEL[r],
  }));
}

export function coerceDraftRarity(
  raw: unknown,
  fallbackWhenImage: ProfileCardRarity | null = 'normal'
): ProfileCardRarity | null {
  const parsed = parseProfileCardRarity(raw);
  if (parsed) return parsed;
  return fallbackWhenImage;
}
